/**
 * Destino de los botones "Ingresar a Google Meet" / "Ingresar a la sesión".
 *
 * Cada psicóloga tiene tres herramientas externas que importan acá:
 *  - `psi meet`      → bandera (`true`/`false`): si ya hace sus sesiones en psi-video.
 *  - `psi meet link` → su link fijo en psi-video (`meet.psimammoliti.com/p/{token}`).
 *  - `google meet`   → su Google Meet de siempre.
 *
 * Con la bandera prendida se entra a la videollamada embebida de la plataforma (`/meet/{token}`):
 * una ruta interna, sin `?authuser` ni pestaña nueva. Con la bandera apagada se mantiene lo de
 * siempre: su Google Meet en una pestaña nueva con `?authuser`. (Mandarla a `/meet/{token}` con la
 * bandera apagada la sacaría de la plataforma, porque esa pantalla la redirige al Google Meet.)
 */
import apiCoreAdapter from '../hooks/apiCoreAdapter';

export type MeetDestination =
  | { kind: 'internal'; path: string }
  | { kind: 'external'; url: string };

const PSI_MEET_FLAG_TAG = 'psi meet';
const PSI_MEET_LINK_TAG = 'psi meet link';
const GOOGLE_MEET_TAG = 'google meet';

const PSI_VIDEO_PERSONAL_LINK =
  /^https?:\/\/meet\.psimammoliti\.com\/p\/([^/?#\s]+)\/?(?:[?#].*)?$/i;

/**
 * Link con el que la psicóloga ingresa a sus sesiones: el de psi-video si tiene la bandera `psi meet`
 * prendida, si no el de Google Meet (y, si no tiene Google Meet pero sí el de psi-video, ese: esa
 * pantalla la lleva a su Meet). `null` si no tiene ninguno. Una sola llamada al backend.
 */
export async function fetchMeetLink(): Promise<string | null> {
  const tools = await apiCoreAdapter.getExternalTools();
  const contentOf = (tag: string): string | null => {
    const tool = tools.find((t) => String(t?.tag ?? '').trim().toLowerCase() === tag);
    return typeof tool?.content === 'string' && tool.content.trim() ? tool.content.trim() : null;
  };
  // Un link con otro esquema (javascript:, data:…) se descarta: es como si no lo tuviera
  const linkOf = (tag: string): string | null => {
    const content = contentOf(tag);
    return content && parseHttpsUrl(content) ? content : null;
  };

  const psiMeetOn = contentOf(PSI_MEET_FLAG_TAG)?.toLowerCase() === 'true';
  const psiLink = linkOf(PSI_MEET_LINK_TAG);
  const googleLink = linkOf(GOOGLE_MEET_TAG);

  if (psiMeetOn && psiLink) return psiLink;
  return googleLink ?? psiLink;
}

/** `null` si el link no es una URL https: no se abre. */
export function resolveMeetDestination(rawUrl: string, email?: string | null): MeetDestination | null {
  const url = rawUrl.trim();
  const match = PSI_VIDEO_PERSONAL_LINK.exec(url);
  if (match) {
    return { kind: 'internal', path: `/meet/${encodeURIComponent(decodeToken(match[1]))}` };
  }
  const parsed = parseHttpsUrl(url);
  if (!parsed) return null;
  if (email) parsed.searchParams.set('authuser', email);
  return { kind: 'external', url: parsed.toString() };
}

function parseHttpsUrl(raw: string): URL | null {
  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'https:' ? parsed : null;
  } catch {
    return null;
  }
}

function decodeToken(token: string): string {
  try {
    return decodeURIComponent(token);
  } catch {
    return token;
  }
}
